import { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/shared/hooks/use-auth'
import { useUser } from '@/shared/hooks/use-user'
import { useBackGesture } from '@/shared/hooks/use-back-gesture'
import { getJwt } from '@/shared/lib/betterAuth/get-jwt'
import {
  CACHE_STALE_TIME,
  CACHE_GC_TIME,
  QUERY_NETWORK_MODE,
  RENTERS_QUERY_KEYS,
} from '@/shared/constants/cache'
import { getRentersPolicy } from '@/services/renters.services'
import { formatDate } from '@/shared/helpers/format-date'
import { ROUTES } from '@/shared/constants/routes'
import { ANIMATION_DURATION_MS, NEXT_TICK_MS } from '@/shared/constants/timings'
import type { RentersPolicy } from '@/shared/types/global.types'
import { sendEvent } from '@/shared/hooks/use-analytics'
import { useForm } from 'react-hook-form'
import type { PersonDetails } from '@/components/molecules/person-form-section'
import type { PetDetails } from '@/components/molecules/pet-form-section'
import { postMessageToParent } from '@/shared/helpers/post-message'
import { ONBOARDING_MESSAGE } from '@/shared/constants/onboarding'

export type RentersFormValues = {
  roommates: PersonDetails[]
  partners: PersonDetails[]
  pets: PetDetails[]
}

const FORM_STEP_COUNT = 2

const FORM_STEP_EVENT_NAMES = [
  'ob_renters_form_1',
  'ob_renters_form_2',
] as const

export function useOnboardingRentersForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user: authUser, loading } = useAuth()
  const { user: userProfile } = useUser()

  const formMethods = useForm<RentersFormValues>({
    defaultValues: {
      roommates: [{ name: '', address: '', email: '' }],
      partners: [{ name: '', address: '', email: '' }],
      pets: [{ name: '', petType: '', age: '', breed: '' }],
    },
  })

  const [currentFormStep, setCurrentFormStep] = useState(0)
  const [isFlipping, setIsFlipping] = useState(false)
  const pendingStepRef = useRef<number | undefined>(undefined)
  const stepOneSubmitRef = useRef<(() => Promise<void>) | undefined>(undefined)
  const stepTwoSubmitRef = useRef<(() => Promise<void>) | undefined>(undefined)
  const [isStepOneDisabled, setIsStepOneDisabled] = useState(false)
  const [isStepOneLoading, setIsStepOneLoading] = useState(false)
  const [isStepTwoLoading, setIsStepTwoLoading] = useState(false)

  const registerStepOneSubmit = useCallback((fn: () => Promise<void>) => {
    stepOneSubmitRef.current = fn
  }, [])

  const registerStepTwoSubmit = useCallback((fn: () => Promise<void>) => {
    stepTwoSubmitRef.current = fn
  }, [])

  const { data: rentersPolices, isLoading: isPolicyLoading } = useQuery<
    RentersPolicy[]
  >({
    queryKey: RENTERS_QUERY_KEYS.policies,
    queryFn: async () => {
      const jwt = await getJwt()
      return getRentersPolicy(jwt)
    },
    enabled: !loading && !!authUser,
    staleTime: CACHE_STALE_TIME,
    gcTime: CACHE_GC_TIME,
    networkMode: QUERY_NETWORK_MODE,
  })

  const policy = rentersPolices?.[0]

  const policyId =
    '#' + (userProfile?.ciUserId ?? userProfile?.insuranceId ?? '')

  const planName = policy?.dwellingType
    ? t(`onboarding.renters.dwellingTypes.${policy.dwellingType}`)
    : ''

  const startDate = formatDate(policy?.startAt)
  const ownerName = userProfile?.fullName ?? userProfile?.givenName ?? ''
  const address = policy?.address?.address ?? ''

  const flipToStep = useCallback((nextStep: number) => {
    pendingStepRef.current = nextStep
    setIsFlipping(true)
    setTimeout(() => {
      document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' })
    }, NEXT_TICK_MS)
  }, [])

  useEffect(() => {
    if (!isFlipping) return
    const timer = setTimeout(() => {
      if (pendingStepRef.current !== undefined) {
        setCurrentFormStep(pendingStepRef.current)
        pendingStepRef.current = undefined
      }
      setIsFlipping(false)
    }, ANIMATION_DURATION_MS)
    return () => clearTimeout(timer)
  }, [isFlipping])

  const handleNext = useCallback(() => {
    if (currentFormStep < FORM_STEP_COUNT - 1) {
      const nextStep = currentFormStep + 1
      sendEvent('ob_renters_form_1_completed')
      const event = FORM_STEP_EVENT_NAMES[nextStep]
      if (event) {
        sendEvent(event)
      }
      flipToStep(nextStep)
      return
    }
    postMessageToParent(ONBOARDING_MESSAGE.COMPLETE_ONBOARDING)
    sendEvent('ob_renters_form_2_completed')
    sendEvent('ob_renters_form_completed')
    navigate({ to: ROUTES.HOME })
  }, [currentFormStep, navigate, flipToStep])

  const handleFooterNext = useCallback(async () => {
    if (currentFormStep === 0 && stepOneSubmitRef.current) {
      await stepOneSubmitRef.current()
      return
    }
    if (currentFormStep === 1 && stepTwoSubmitRef.current) {
      await stepTwoSubmitRef.current()
      return
    }
    handleNext()
  }, [currentFormStep, handleNext])

  const handleBack = useCallback(() => {
    if (currentFormStep > 0) {
      flipToStep(currentFormStep - 1)
      return
    }
    navigate({ to: ROUTES.ONBOARDING_RENTERS })
  }, [currentFormStep, navigate, flipToStep])

  const handleSkip = useCallback(() => {
    postMessageToParent(ONBOARDING_MESSAGE.COMPLETE_ONBOARDING)
    sendEvent('ob_renters_form_skipped')
    navigate({ to: ROUTES.HOME })
  }, [navigate])

  useBackGesture(handleBack, currentFormStep > 0)

  return {
    currentFormStep,
    isFlipping,
    handleNext,
    handleFooterNext,
    registerStepOneSubmit,
    registerStepTwoSubmit,
    isStepOneDisabled,
    setIsStepOneDisabled,
    isStepOneLoading,
    setIsStepOneLoading,
    isStepTwoLoading,
    setIsStepTwoLoading,
    handleBack,
    handleSkip,
    isPolicyLoading,
    planName,
    ownerName,
    policyId,
    startDate,
    address,
    formMethods,
  }
}
